import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import ToolbarButton from 'components/layout/ToolbarButton';
import { MandateConsumerRenderProps } from 'components/MandateContext';
import { PatientConsumerRenderProps } from 'components/patient/PatientContext';
import PatientCostEstimationsView from 'components/patient/PatientCostEstimationsView';
import PatientSearch from 'components/patient/PatientSearch';
import UpsertPatient from 'components/patient/upsert/UpsertPatient';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Icon, Popup } from 'semantic-ui-react';
import { getAllCountries } from 'services/countryServices';
import { getAllLanguages } from 'services/languageServices';
import { findPatientById, healthCardSearchPatients } from 'services/patientServices';
import styled from 'styled-components';
import {
  CountryDto,
  LanguageDto,
  PatientDto,
  PatientHealthCardSearchRequest,
  UpsertPatientDto
} from 'ts-types/api.types';
import { ChromeExtensionStatus, getChromeExtensionStatus, readHealthCard } from 'utils/chomeExtensionUtils';
import { noop } from 'utils/functionUtils';

const PatientToolbarContainer = styled.div`
  width: 70%;
  display: inline;
  flex: 1;
  text-align: right;
`;

const InfoDiv = styled.div`
  & .info-message {
    margin-top: 1rem;
    margin-bottom: 1.2rem;
    margin-left: 0.1rem;
    min-height: 105px;
  }

  & .info-message.minimal-info-message {
    min-height: 2rem;
  }

  & .info-message .download-link {
    margin-top: 1.5rem;
  }

  & .actions .button:first-child {
    margin-left: 0;
  }
`;

enum CardReaderState {
  INITIAL,
  NO_EXTENSION,
  NO_HOST_APPLICATION,
  OUTDATED_EXTENSION,
  OUTDATED_HOST_APP,
  NO_CARD,
  NO_PATIENT_FOUND,
  CARD_READ,
  ERROR
}

interface Props extends AuthConsumerRenderProps,
    PatientConsumerRenderProps,
    MessageSourceProps,
    MandateConsumerRenderProps {

  isHome: boolean
}

interface State {
  patientSearchOpen: boolean,
  patientSearchInitialPatients?: Array<PatientDto>,
  patientAddEditOpen: boolean,
  patientCostEstimationsOpen: boolean,
  countries: Array<CountryDto>,
  languages: Array<LanguageDto>,
  patient?: UpsertPatientDto,
  patientId: number,
  searchFromHealthCardPopupOpen: boolean,
  searchFromHealthCardPopupStatus: CardReaderState,
  cancelTokenSource: CancelTokenSource
}

class PatientToolbar extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const {getAllMandates} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    getAllMandates();

    getAllCountries(cancelTokenSource).then(response => {
      if (!this.mounted) {
        return
      }

      this.setState({
        countries: response
      });
    }).catch(noop);

    getAllLanguages(cancelTokenSource).then(response => {
      if (!this.mounted) {
        return
      }

      this.setState({
        languages: response
      });
    }).catch(noop);

    this.state = {
      patientSearchOpen: false,
      patientAddEditOpen: false,
      patientCostEstimationsOpen: false,
      countries: [],
      languages: [],
      patient: undefined,
      patientId: -1,
      searchFromHealthCardPopupOpen: false,
      searchFromHealthCardPopupStatus: CardReaderState.INITIAL,
      cancelTokenSource
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  togglePatientSearchModal = (initialPatients?: Array<PatientDto>) => {

    this.setState(({patientSearchOpen}: State) => ({
      patientSearchOpen: !patientSearchOpen,
      patientSearchInitialPatients: initialPatients,
    }));
  };

  togglePatientEditModal = () => {
    const {currentPatient: patient} = this.props;
    const {cancelTokenSource} = this.state;

    findPatientById(cancelTokenSource, patient!.id).then(response => {
      this.setState({
        patientId: patient!.id,
        patient: response
      });
      this.togglePatientModal();
    }).catch(noop);
  };

  togglePatientModal = () => {
    this.setState(({patientAddEditOpen}: State) => ({
      patientAddEditOpen: !patientAddEditOpen
    }));
  };

  togglePatientAddModal = () => {
    this.togglePatientModal();
    this.setState({
      patientId: -1,
      patient: undefined
    });
  };

  togglePatientCostEstimationsModal = () => {
    this.setState(({patientCostEstimationsOpen}: State) => ({
      patientCostEstimationsOpen: !patientCostEstimationsOpen
    }));
  };

  onPatientSave = (patient: PatientDto) => {
    this.props.changePatient(patient);
    this.togglePatientModal();
  };

  onPatientSelected = (patient?: PatientDto) => {
    this.props.changePatient(patient);
    this.togglePatientSearchModal();
  };

  triggerSearchPatientFromHealthCard = async () => {
    const {currentPatient: patient, config} = this.props;
    const {cancelTokenSource} = this.state;

    this.setState({
      searchFromHealthCardPopupOpen: true,
      searchFromHealthCardPopupStatus: CardReaderState.INITIAL
    });

    const extStatus = await getChromeExtensionStatus(config);

    if (ChromeExtensionStatus.NO_EXTENSION === extStatus) {
      this.setHealthCardReaderStatus(CardReaderState.NO_EXTENSION);
    } else if (ChromeExtensionStatus.OUTDATED_EXTENSION === extStatus) {
      this.setHealthCardReaderStatus(CardReaderState.OUTDATED_EXTENSION);
    } else if (ChromeExtensionStatus.NO_HOST_APPLICATION === extStatus) {
      this.setHealthCardReaderStatus(CardReaderState.NO_HOST_APPLICATION);
    } else if (ChromeExtensionStatus.OUTDATED_HOST_APP === extStatus) {
      this.setHealthCardReaderStatus(CardReaderState.OUTDATED_HOST_APP);
    } else if (ChromeExtensionStatus.OK === extStatus) {

      const cardReaderResponse: any = await readHealthCard();

      const payload = cardReaderResponse.payload;
      if (cardReaderResponse.status === 'ERROR' && payload.errorCode === "NO_PATIENT_DATA") {
        this.setHealthCardReaderStatus(CardReaderState.NO_CARD);
      } else if (cardReaderResponse.status === 'ERROR') {
        this.setHealthCardReaderStatus(CardReaderState.ERROR);
      } else if (cardReaderResponse.status === 'RESULT') {
        const responseCardData = payload;

        const searchRequest: PatientHealthCardSearchRequest = {
          firstName: responseCardData.firstName,
          lastName: responseCardData.lastName,
          birthDate: responseCardData.dateOfBirth,
          avhNumber: responseCardData.insuranceNumber
        };

        const foundPatients = await healthCardSearchPatients(searchRequest, cancelTokenSource);

        if (foundPatients.length < 1) {
          this.setHealthCardReaderStatus(CardReaderState.NO_PATIENT_FOUND)
        } else if (foundPatients.length === 1) {

          this.setState({
            searchFromHealthCardPopupOpen: false
          });

          this.props.changePatient(foundPatients[0]);
        } else if (foundPatients.length > 1) {

          this.setState({
            searchFromHealthCardPopupOpen: false
          });

          this.togglePatientSearchModal(foundPatients);
        }

      }
    }
  }

  setHealthCardReaderStatus(status: CardReaderState) {
    this.setState({
      searchFromHealthCardPopupStatus: status
    });
  }

  closeSearchFromHealthCardPopup = () => {
    this.setState({
      searchFromHealthCardPopupOpen: false
    });
  }

  render() {

    const {
      patientSearchOpen, patientSearchInitialPatients, patientAddEditOpen, countries, languages,
      patient, patientId, patientCostEstimationsOpen,
      searchFromHealthCardPopupOpen
    } = this.state;
    const {currentMandates, getAllMandates, currentPatient, getMessage} = this.props;

    return (
        <PatientToolbarContainer>
          <ToolbarButton iconName="plus"
                         iconSize="large"
                         onClick={this.togglePatientAddModal}
                         dataTooltip={getMessage('patient.toolbar.add')}
          />
          <ToolbarButton iconName="search"
                         iconSize="large"
                         onClick={() => this.togglePatientSearchModal()}
                         dataTooltip={getMessage('patient.toolbar.search')}
          />
          <Popup
              trigger={
                <ToolbarButton iconName="search"
                               iconSize="large"
                               onClick={() => this.triggerSearchPatientFromHealthCard()}
                               customTrigger={(
                                   <Icon.Group size="large">
                                     <Icon name="id card outline" color="grey"/>
                                   </Icon.Group>
                               )}
                               dataTooltip={getMessage('patient.toolbar.searchFromHealthCard')}
                />
              }
              open={searchFromHealthCardPopupOpen}
              content={this.renderSearchFromHealthCardPopupContent()}
              on="click"
              onOpen={() => {
              }}
              onClose={() => {
              }}
              size="small"
              position="bottom center"
          />
          {
            this.props.isHome &&
            <>
              <ToolbarButton iconName="edit outline"
                             iconSize="large"
                             disabled={!currentPatient}
                             onClick={this.togglePatientEditModal}
                             dataTooltip={getMessage('patient.toolbar.edit')}
              />
              <ToolbarButton iconName="money bill alternate outline"
                             iconSize="large"
                             disabled={!currentPatient}
                             onClick={this.togglePatientCostEstimationsModal}
                             dataTooltip={getMessage('costEstimations')}
              />
            </>
          }
          <ToolbarButton iconName="refresh"
                         iconSize="large"
                         disabled={!currentPatient}
                         onClick={() => this.props.onPatientRefresh(currentPatient)}
                         dataTooltip={getMessage('patient.toolbar.refresh')}
          />
          {
            patientSearchOpen &&
            <PatientSearch
                initialPatients={patientSearchInitialPatients || []}
                onPatientSelected={this.onPatientSelected}
                patientSearchOpen={patientSearchOpen}
                toggleModal={this.togglePatientSearchModal}
            />
          }
          {
            patientAddEditOpen &&
            <UpsertPatient
                modalOpen={patientAddEditOpen}
                toggleModal={this.togglePatientModal}
                countries={countries}
                languages={languages}
                patient={patient}
                patientId={patientId}
                onPatientSave={this.onPatientSave}
                currentMandates={currentMandates}
                getAllMandates={getAllMandates}
            />
          }
          {
            patientCostEstimationsOpen &&
            <PatientCostEstimationsView
                modalOpen={patientCostEstimationsOpen}
                onCloseModal={this.togglePatientCostEstimationsModal}
                patientId={currentPatient ? currentPatient.id : -1}
            />
          }
        </PatientToolbarContainer>
    );
  }

  renderSearchFromHealthCardPopupContent = () => {
    const {getMessage} = this.props;
    const {searchFromHealthCardPopupStatus} = this.state;

    let content = () => {
      switch (searchFromHealthCardPopupStatus) {
        case CardReaderState.INITIAL :
          return <InfoDiv>
            <div className="info-message minimal-info-message">
              {getMessage('patient.searchFromHealthCardPopup.state.initial')}
            </div>
          </InfoDiv>;

        case CardReaderState.NO_EXTENSION :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_EXTENSION')}
              <div className="download-link">
                <a href="https://chrome.google.com/webstore/detail/iggeppehjnmjeeicldkpjopdofglljei?authuser=0&hl=en"
                   target="_blank">{getMessage('nativeTools.download.chromeExtension')}</a>
              </div>
            </div>
          </InfoDiv>;

        case CardReaderState.OUTDATED_EXTENSION :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_EXTENSION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.chromeExtension')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case CardReaderState.NO_HOST_APPLICATION :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_HOST_APPLICATION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case CardReaderState.OUTDATED_HOST_APP :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_HOST_APP')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case CardReaderState.NO_CARD :
          return <InfoDiv>
            <div className="info-message minimal-info-message">
              {getMessage('patient.searchFromHealthCardPopup.state.noCard')}
            </div>
          </InfoDiv>;

        case CardReaderState.NO_PATIENT_FOUND :
          return <InfoDiv>
            <div className="info-message minimal-info-message">
              {getMessage('patient.searchFromHealthCardPopup.state.noPatientFound')}
            </div>
          </InfoDiv>;
      }
    }

    return (
        <div>
          {content()}
          <div>
            <Button type="button" compact onClick={this.closeSearchFromHealthCardPopup}>Close</Button>
          </div>
        </div>
    )
  }

}

export default withAuthContext(withMessages(PatientToolbar));
